import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import { CalendarDays } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import SelectField from '../common/SelectField.js';
import expenseService from '../../services/ExpenseService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatHubBranchLabel, filterShipmentLocations } from '../../utils/destinationType.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { getLocalDateString } from '../../utils/localDate.js';

const isOwnBranch = (hub) => String(hub?.ownership_type || 'OWN').trim().toUpperCase() !== 'AGENT';

const BILL_NAME_OPTIONS = [{ value: 'Office', label: 'Office' }];

const CATEGORY_LABELS = {
  hamali_payment: 'Hamali Payment',
  office_other: 'Other Expenses',
  other_expenses: 'Other Expenses',
  freight_forwarding: 'Freight Forwarding Charges',
  rjm_vsp_forwarding: 'RJM & VSP Forwarding Charges',
  nlr_hire_truck_charges: 'NLR Hire Truck Charges',
  vja_hire_truck_charges: 'VJA Hire Truck Charges',
  vja_hire_truck_advance: 'VJA Hire Truck Charges',
  vja_hire_truck_balance: 'VJA Hire Truck Charges',
  delivery_expenses: 'Delivery Expenses',
  cc_expenses: 'CC Expenses',
  pc_expenses: 'PC Expenses',
  pick_up_expenses: 'Pick Up Expenses',
  tea_coffee: 'Tea/Coffee',
  office_supplies: 'Office Supplies',
  internet_phone: 'Internet/Phone',
  transportation: 'Transportation',
  cleaning: 'Cleaning',
  printing_stationery: 'Printing & Stationery',
};

const emptyForm = {
  name: 'Office',
  category: 'rjm_vsp_forwarding',
  amount: '',
  payee: '',
  location_hub_id: '',
  bill_date: getLocalDateString(),
};

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

const categoryLabel = (value) => {
  const key = String(value || '').trim();
  if (CATEGORY_LABELS[key]) return CATEGORY_LABELS[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()) || '—';
};

const compactInputClass =
  'w-full min-w-0 px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400';
const compactLabelClass = 'block text-[10px] sm:text-[11px] font-medium text-gray-600 mb-0.5';
const compactSelectTrigger =
  'w-full min-w-0 px-2.5 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none';

const RecurringWeeklyBills = () => {
  const { user } = useAuth();
  const canCreateBills = user?.role !== 'Owner';
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [tab, setTab] = useState('all');
  const [hubFilter, setHubFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, categoriesRes, hubsRes] = await Promise.all([
        expenseService.getRecurringTemplates(),
        expenseService.getCategories(),
        locationHubService.getHubsForSelect({ brief: 1 }),
      ]);
      setRows(templatesRes.data || []);
      const HIDDEN_CATEGORIES = new Set(['vja_hire_truck_advance', 'vja_hire_truck_balance']);
      const fromApi = (categoriesRes.data || [])
        .map((c) => ({
          value: c.value || c,
          label: CATEGORY_LABELS[c.value || c] || c.label || categoryLabel(c),
        }))
        .filter((c) => !HIDDEN_CATEGORIES.has(c.value));
      const byValue = new Map(fromApi.map((c) => [c.value, c]));
      [
        { value: 'nlr_hire_truck_charges', label: 'NLR Hire Truck Charges' },
        { value: 'vja_hire_truck_charges', label: 'VJA Hire Truck Charges' },
        { value: 'freight_forwarding', label: 'Freight Forwarding Charges' },
        { value: 'rjm_vsp_forwarding', label: 'RJM & VSP Forwarding Charges' },
        { value: 'delivery_expenses', label: 'Delivery Expenses' },
        { value: 'cc_expenses', label: 'CC Expenses' },
        { value: 'pc_expenses', label: 'PC Expenses' },
        { value: 'pick_up_expenses', label: 'Pick Up Expenses' },
        { value: 'hamali_payment', label: 'Hamali Payment' },
        { value: 'office_other', label: 'Other Expenses' },
      ].forEach((item) => {
        byValue.set(item.value, { ...(byValue.get(item.value) || {}), ...item });
      });
      const preferred = [
        'nlr_hire_truck_charges',
        'vja_hire_truck_charges',
        'freight_forwarding',
        'rjm_vsp_forwarding',
        'delivery_expenses',
        'cc_expenses',
        'pc_expenses',
        'pick_up_expenses',
        'hamali_payment',
        'office_other',
      ];
      setCategories([
        ...preferred.map((value) => byValue.get(value)).filter(Boolean),
        ...Array.from(byValue.values()).filter((item) => !preferred.includes(item.value)),
      ]);
      setHubs(filterShipmentLocations(hubsRes.data?.hubs || []).filter(isOwnBranch));
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load daily bills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const active = rows.filter((row) => row.is_active).length;
    return {
      all: rows.length,
      active,
      inactive: rows.length - active,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (tab === 'active' && !row.is_active) return false;
      if (tab === 'inactive' && row.is_active) return false;
      if (hubFilter && String(row.location_hub_id || '') !== String(hubFilter)) return false;
      return true;
    });
  }, [rows, tab, hubFilter]);

  const aggregates = useMemo(() => {
    const activeRows = rows.filter((row) => row.is_active);
    const activeTotal = activeRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const visibleTotal = filteredRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    return {
      weekTotal: activeTotal,
      activeCount: activeRows.length,
      inactiveCount: rows.length - activeRows.length,
      visibleTotal,
    };
  }, [rows, filteredRows]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.amount) {
      Alert.error('Name and amount are required');
      return;
    }
    if (form.category === 'freight_forwarding' && !form.location_hub_id) {
      Alert.error('Select the hub for Freight Forwarding charges');
      return;
    }
    const routeCategories = new Set([
      'nlr_hire_truck_charges',
      'vja_hire_truck_charges',
      'freight_forwarding',
      'rjm_vsp_forwarding',
      'delivery_expenses',
    ]);
    if (!routeCategories.has(form.category) && !form.location_hub_id) {
      Alert.error('Select the hub / branch. This amount goes to that office on Profit & Loss.');
      return;
    }
    try {
      setSaving(true);
      await expenseService.createRecurringTemplate({
        ...form,
        amount: Number(form.amount),
        frequency: 'daily',
        bill_date: form.bill_date || getLocalDateString(),
      });
      Alert.success('Daily bill added. It will show on Profit & Loss for that date.');
      setForm({ ...emptyForm, bill_date: getLocalDateString() });
      load();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to save daily bill');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      setBusyId(row.id);
      await expenseService.updateRecurringTemplate(row.id, { is_active: !row.is_active });
      await load();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update daily bill');
    } finally {
      setBusyId('');
    }
  };

  const statusBadge = (isActive) =>
    isActive
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : 'bg-gray-100 text-gray-600 border-gray-200';

  const hubOptions = [
    { value: '', label: 'All branches' },
    ...hubs.map((h) => ({
      value: String(h.id),
      label: formatHubBranchLabel(h) || h.name || h.location,
    })),
  ];

  const metricCards = [
    {
      key: 'week',
      label: 'Active total',
      value: fullCurrency(aggregates.weekTotal),
      hint: 'Active only',
      box: 'border-amber-100',
      valueClass: 'text-amber-800',
    },
    {
      key: 'active',
      label: 'Active bills',
      value: aggregates.activeCount,
      hint: `${counts.active} of ${counts.all}`,
      box: 'border-sky-100',
      valueClass: 'text-sky-800',
    },
    {
      key: 'inactive',
      label: 'Inactive bills',
      value: aggregates.inactiveCount,
      hint: `${counts.inactive} of ${counts.all}`,
      box: 'border-gray-200',
      valueClass: 'text-gray-800',
    },
    {
      key: 'shown',
      label: 'Shown in tab',
      value: fullCurrency(aggregates.visibleTotal),
      hint: `${filteredRows.length} ${filteredRows.length === 1 ? 'bill' : 'bills'}`,
      box: 'border-emerald-100',
      valueClass: 'text-emerald-800',
    },
  ];

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Daily Bills"
        subtitle="Enter the day's charges here. Freight Forwarding and RJM & VSP forwarding post to Profit & Loss for that date."
        icon={CalendarDays}
        backTo="/expenses"
        className="mb-2"
      />

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-2 sm:p-2.5 mb-2">
        <div className="flex flex-col md:flex-row md:items-end gap-2">
          <div className="w-full md:w-44 shrink-0">
            <SelectField
              variant="filter"
              label="Hub / Branch"
              labelClassName={compactLabelClass}
              triggerClassName={compactSelectTrigger}
              value={hubFilter}
              onChange={(e) => setHubFilter(e.target.value)}
              placeholder="All branches"
              emptyLabel="All branches"
              options={hubOptions}
            />
          </div>
          <div className="flex-1 min-w-0">
            <label className={compactLabelClass}>Status</label>
            <div className="flex gap-1 p-0.5 rounded-lg bg-gray-50 border border-gray-200">
              {TABS.map((item) => {
                const active = tab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTab(item.id)}
                    className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[11px] sm:text-xs font-semibold transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {item.label}
                    <span className={`min-w-[1.1rem] rounded-full px-1 text-[10px] font-bold ${
                      active ? 'bg-blue-50 text-blue-700' : 'bg-gray-200/80 text-gray-600'
                    }`}>
                      {counts[item.id]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 sm:gap-2 mt-2">
          {metricCards.map((card) => (
            <div key={card.key} className={`rounded-lg border bg-white px-2.5 py-1.5 ${card.box}`}>
              <p className="text-[10px] text-gray-500 leading-tight">{card.label}</p>
              <p className={`text-sm sm:text-base font-semibold tabular-nums leading-tight ${card.valueClass}`}>
                {card.value}
              </p>
              <p className="text-[10px] text-gray-400 leading-tight">{card.hint}</p>
            </div>
          ))}
        </div>
      </div>

      {canCreateBills && (
      <form
        onSubmit={handleCreate}
        className="bg-white rounded-xl border border-gray-100 shadow-sm p-2 sm:p-2.5 mb-2"
      >
        <p className="text-xs font-semibold text-gray-800 mb-1.5">Add daily bill</p>
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-1.5 sm:gap-2 items-end">
          <div className="min-w-0">
            <label className={compactLabelClass}>Bill date</label>
            <input
              name="bill_date"
              type="date"
              value={form.bill_date}
              onChange={handleChange}
              className={compactInputClass}
            />
          </div>
          <div className="col-span-2 lg:col-span-1 min-w-0">
            <SelectField
              variant="filter"
              label="Bill name"
              labelClassName={compactLabelClass}
              triggerClassName={compactSelectTrigger}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Office"
              options={BILL_NAME_OPTIONS}
            />
          </div>
          <div className="min-w-0">
            <SelectField
              variant="filter"
              label="Category"
              labelClassName={compactLabelClass}
              triggerClassName={compactSelectTrigger}
              name="category"
              value={form.category}
              onChange={handleChange}
              options={categories.map((c) => ({
                value: c.value || c,
                label: c.label || categoryLabel(c),
              }))}
            />
          </div>
          <div className="min-w-0">
            <label className={compactLabelClass}>Amount</label>
            <input
              name="amount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={form.amount}
              onChange={handleChange}
              placeholder="0.00"
              className={compactInputClass}
            />
          </div>
          <div className="min-w-0">
            <label className={compactLabelClass}>Payee</label>
            <input
              name="payee"
              value={form.payee}
              onChange={handleChange}
              placeholder="Optional"
              className={compactInputClass}
            />
          </div>
          <div className="min-w-0">
            <SelectField
              variant="filter"
              label="Hub / Branch"
              labelClassName={compactLabelClass}
              triggerClassName={compactSelectTrigger}
              name="location_hub_id"
              value={form.location_hub_id}
              onChange={handleChange}
              placeholder="All branches"
              emptyLabel="All branches"
              options={hubOptions}
            />
          </div>
          <div className="col-span-2 lg:col-span-1">
            <Button type="submit" size="sm" loading={saving} className="w-full justify-center h-[34px] sm:h-[36px]">
              {saving ? 'Saving...' : 'Add bill'}
            </Button>
          </div>
        </div>
      </form>
      )}

      {loading ? (
        <Loader fullScreen={false} message="Loading daily bills..." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {!filteredRows.length ? (
            <p className="text-xs sm:text-sm text-gray-400 text-center py-6">No daily bills in this tab</p>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-[720px] w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Date</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Bill name</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Category</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Hub / Branch</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Payee</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Status</th>
                      <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Amount</th>
                      <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredRows.map((row) => (
                      <tr key={row.id} className="hover:bg-sky-50/40">
                        <td className="px-3 py-2 whitespace-nowrap">{row.bill_date || '—'}</td>
                        <td className="px-3 py-2 font-medium text-gray-800">{row.name}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{categoryLabel(row.category)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.location_hub_name || 'All branches'}</td>
                        <td className="px-3 py-2">{row.payee || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-semibold ${statusBadge(row.is_active)}`}>
                            {row.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">
                          {fullCurrency(row.amount)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {canCreateBills && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={busyId === row.id}
                            onClick={() => toggleActive(row)}
                          >
                            {row.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden p-2 sm:p-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {filteredRows.map((row) => (
                  <div
                    key={`card-${row.id}`}
                    className="rounded-lg border border-gray-100 border-l-4 border-l-blue-500 bg-white p-2.5 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-800 break-words min-w-0">{row.name}</p>
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold shrink-0 ${statusBadge(row.is_active)}`}>
                        {row.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-base font-semibold text-gray-900 tabular-nums mt-0.5">{fullCurrency(row.amount)}</p>
                    <div className="grid grid-cols-2 gap-1.5 mt-1.5 text-[11px]">
                      <div>
                        <span className="text-gray-500 block">Date</span>
                        <p className="font-medium text-gray-800">{row.bill_date || '—'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Category</span>
                        <p className="font-medium text-gray-800">{categoryLabel(row.category)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Hub / Branch</span>
                        <p className="font-medium text-gray-800">{row.location_hub_name || 'All branches'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500 block">Payee</span>
                        <p className="font-medium text-gray-800">{row.payee || '—'}</p>
                      </div>
                    </div>
                    {canCreateBills && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="w-full justify-center mt-2"
                      disabled={busyId === row.id}
                      onClick={() => toggleActive(row)}
                    >
                      {row.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default RecurringWeeklyBills;
